import * as d3 from 'd3';
import { booleanClockwise } from '@turf/turf';

import { calculateCentroid } from './mapApiHelpers';

/*
  When using these functions, a "ring" is an array of coordinates that defines a polygon by
  connecting the coordinates consecutively.
  "Winding" is the direction of the connections between those coordinates in the ring, clockwise or counter-clockwise.
  Polygons that have cut-out sections in them have inner rings that define the shape of the cut-out polygon.
*/

export const outerRingIsClockwise = shape => {
  if (!shape.coordinates || shape.coordinates.length === 0) return false;
  let outerRing;
  if (shape.type === 'MultiPolygon') {
    [[outerRing]] = shape.coordinates;
  } else [outerRing] = shape.coordinates;
  // A degenerate ring (missing, or too few points for turf's own line/ring
  // validation) has no clockwise-ness — `booleanClockwise` throws
  // "line is required" on `undefined`/an empty ring rather than returning a
  // sensible default, so this is checked explicitly instead of forwarding
  // to it.
  if (!outerRing || outerRing.length === 0) return false;

  return booleanClockwise(outerRing);
};

// Same degenerate-ring guard as `outerRingIsClockwise` above — an empty
// ring has no winding direction, and `booleanClockwise` throws on one.
export const isCounterClockwiseOuterRing = ring =>
  ring.length > 0 && !booleanClockwise(ring);

// outer rings have to be wound opposite of inner rings
export const svgWindCoordinates = coordinates =>
  coordinates.map(ring =>
    isCounterClockwiseOuterRing(ring) ? [...ring].reverse() : ring
  );

export const svgWindPolygon = polygon => ({
  ...polygon,
  coordinates: svgWindCoordinates(polygon.coordinates)
});

export const svgWindFeature = geometry => {
  if (!geometry.coordinates) return geometry;
  if (geometry.type === 'MultiPolygon') {
    return {
      ...geometry,
      coordinates: geometry.coordinates.map(polygonRings =>
        svgWindCoordinates(polygonRings)
      )
    };
  }
  // will always need to go through winding process if there is more than 1 ring
  return outerRingIsClockwise(geometry) && geometry.coordinates.length === 0
    ? geometry
    : svgWindPolygon(geometry);
};

export const renderFeaturesToSvg = (
  features,
  svgSelector,
  { border, color, extent }
) => {
  const filteredFeatures = features.filter(
    feature => feature.geometry?.coordinates?.length > 0
  );

  if (filteredFeatures.length === 0) {
    return;
  }

  const featureCollection = {
    type: 'FeatureCollection',
    features: filteredFeatures.map(feature => ({
      ...feature,
      geometry: svgWindFeature(feature.geometry)
    }))
  };
  const cent = calculateCentroid(featureCollection);
  if (!cent?.geometry?.coordinates) {
    return;
  }
  const projection = d3
    .geoMercator()
    .center(cent.geometry.coordinates)
    .fitExtent(extent, featureCollection);
  const geoGen = d3.geoPath().projection(projection);
  const svgGroup = d3.select(svgSelector).select('g');
  const updater = svgGroup.selectAll('path').data(featureCollection.features);
  updater
    .enter()
    .append('path')
    .merge(updater)
    .attr('d', geoGen)
    .attr('stroke', border ?? color)
    .attr('fill', color);
  updater.exit().remove();
};
